import axios from "axios";
import {
    AuthenticationResult,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalInstance } from "../config/authConfig"; // Import your MSAL instance
import { AZURE_APP_CLIENT_ID, BASE_URL } from "./constants";
import { appInsights } from "../config/appInsights";

const apiClient = axios.create({
    baseURL: `${BASE_URL}/`, // Replace with your API base URL
});

// Function to get a new access token
const getAccessToken = async (): Promise<string | null> => {
    const account = msalInstance.getActiveAccount();
    if (!account) {
        console.warn("No active account found. Please log in.");
        return null;
    }

    try {
        const response: AuthenticationResult =
            await msalInstance.acquireTokenSilent({
                account: account, // Use the active account
                scopes: [`api://${AZURE_APP_CLIENT_ID}/access_as_user`], // Replace with the required scopes for your API
            });
        return response.accessToken; // Return the access token
    } catch (error) {
        console.error("Failed to acquire token silently:", error);

        // Optionally, handle token refresh failure by redirecting to login
        if (error instanceof InteractionRequiredAuthError) {
            console.warn("Interaction required. Redirecting to login...");
            msalInstance.loginRedirect();
        }

        throw error;
    }
};

// Request interceptor to attach the access token
apiClient.interceptors.request.use(
    async (config) => {
        const traceId = appInsights.context.telemetryTrace.traceID;
        const spanId = appInsights.context.telemetryTrace.parentID;
        // Add distributed tracing headers
        config.headers["Request-Id"] = spanId;
        config.headers["traceparent"] = `00-${traceId}-${spanId}-01`;

        appInsights.trackEvent({
            name: "Web API Request",
            properties: {
                method: config.method,
                url: config.url,
                data: JSON.stringify(config.data),
                headers: JSON.stringify(config.headers),
            },
        });

        const token = await getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        appInsights.trackException({
            error,
            severityLevel: 2,
            properties: {
                url: error.config?.url,
                message: error.message,
                response: JSON.stringify(error.response?.data),
            },
        });

        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh on 401 errors
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        appInsights.trackException({ error, severityLevel: 3 });
        if (error.response?.status === 401) {
            console.warn("Access token expired. Attempting to refresh token...");
            try {
                const token = await getAccessToken();
                if (token) {
                    // Retry the original request with the new token
                    error.config.headers.Authorization = `Bearer ${token}`;
                    return apiClient.request(error.config);
                }
            } catch (refreshError) {
                console.error("Token refresh failed:", refreshError);
                // Optionally, redirect to login if token refresh fails
                msalInstance.loginRedirect();
            }
        }
        return Promise.reject(error);
    }
);

export default apiClient;
